<template>
  <div v-if="is_valid_brick" class="d-flex flex-row flex-wrap">
    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('prediction_column')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-primary"
        />
        <text-fitter
          :value="prediction_column"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>

    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('score_criteria')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-success"
        />
        <text-fitter
          :value="score_criterion"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>

    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('nb_elementary_models_trained')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-warning"
        />
        <text-fitter
          :value="models_trained"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>

    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('summary_proba_found')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-danger"
        />
        <b-table
          striped
          hover
          :items="summary_probabilities_observation_percentage"
        ></b-table>
        <b-table
          striped
          hover
          :items="summary_probabilities_probability_threshold"
        ></b-table>
      </b-card>
    </div>

    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('feature_importance')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-info"
        />

        <ul>
          <li
            v-for="feature_info in summary_feature_importance"
            :key="feature_info.name"
          >
            {{ feature_info.name }} - {{ feature_info.value }}
          </li>
        </ul>
      </b-card>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "prediction_column": "Prediction column",
    "score_criteria": "Score criteria",
    "nb_elementary_models_trained": "Number of elementary models trained",
    "summary_proba_found": "Summary of probabilities found",
    "not_found": "Not found",
    "waiting_computation": "Waiting for computation"
  },
  "fr": {
    "prediction_column": "Colonne à prédire",
    "score_criteria": "Critère de score",
    "nb_elementary_models_trained": "Nombre de modèles élementaires entrainés",
    "summary_proba_found": "Résumé des probabilités trouvées",
    "not_found": "Non renseignée",
    "waiting_computation": "En attente de calcul"
  }
}
</i18n>

<script>
import _ from 'lodash'

import { extended_dict_keys } from '@/utils.js'
import { subscription_mixin, summary_mixin } from '@/components/mixin.js'
import TextFitter from './text_fitter.vue'

// *****************************************************************************

export default {
  name: 'SummaryModel',
  components: { TextFitter },
  mixins: [subscription_mixin, summary_mixin],
  props: ['project_id', 'brick_id'],
  computed: {
    prediction_column() {
      // TODO : For now, we return the last modification date
      // but it seems to always
      // be the current time : don't know why + decalage horaire
      let target_col = this.cur_brick.target_column
      let res =
        target_col !== -1 ? (target_col || {}).name : this.$t('not_found')
      return res
    },
    tracking_info() {
      return this.fetch_poid_tracking_info('tracking_info', this.poid) || {}
    },
    model_tracking_info() {
      return (
        this.fetch_poid_tracking_info(
          'model_tracking_info',
          this.tracking_info.search_poid
        ) || {}
      )
    },
    caruana_search_tracking_info() {
      let res = this.fetch_poid_tracking_info(
        'caruana_search_tracking_info',
        this.model_tracking_info.caruana_search
      )
      return res
    },
    caruana_model_searches() {
      const model_searches = (this.caruana_search_tracking_info || {})
        .model_searches
      let res = {}
      for (const [name, poid] of _.entries(model_searches)) {
        res[name] = this.fetch_poid_tracking_info(
          `caruana_model_searches_${name}`,
          poid
        )
      }
      return res
    },
    caruana_single_cv() {
      let res = {}
      for (const [name, info] of _.entries(this.caruana_model_searches || {})) {
        res[name] = this.fetch_poid_tracking_info(
          `caruana_single_cv${name}`,
          (info || {}).single_cv
        )
      }
      return res
    },
    caruana_full_cv() {
      let res = {}
      for (const [name, info] of _.entries(this.caruana_model_searches || {})) {
        res[name] = this.fetch_poid_tracking_info(
          `caruana_full_cv${name}`,
          (info || {}).full_cv
        )
      }
      return res
    },
    score_criterion() {
      let scorer = this.cur_brick.scorer
      let res = scorer !== null ? scorer : this.$t('not_found')
      return res
    },
    total_models_to_train() {
      let brick = this.cur_brick
      let res = 0
      res += brick.nb_single_cv_iters || 0
      res += (brick.nb_folds || 0) * (brick.nb_full_cv_iters || 0)
      let nb_models = brick.models.length || 0
      res *= nb_models
      return res
    },
    models_trained() {
      let total_models_to_train = this.total_models_to_train
      let cscv = this.caruana_single_cv
      let cfcv = this.caruana_full_cv
      let nb_models_trained = 0
      for (const [model_name, iter_info] of _.entries(cscv || {})) {
        let iterations = (iter_info || {}).iterations || []
        for (const iter of iterations) {
          let val = iter.value
          if (val !== null) {
            nb_models_trained += 1
          }
        }
      }
      for (const [model_name, iter_info] of _.entries(cfcv || {})) {
        let iterations = (iter_info || {}).iterations || []
        for (const iter of iterations) {
          let vals = (iter || []).values
          for (const val of vals) {
            if (!_.isNaN(val)) {
              nb_models_trained += 1
            }
          }
        }
      }
      let percent_done = (
        (100.0 * nb_models_trained) /
        total_models_to_train
      ).toFixed(2)
      let res = `${nb_models_trained} / ${total_models_to_train} (${percent_done}%)`
      return res
    },
    probability_poid() {
      return this.tracking_info.probability_poid || -1
    },
    probability_instantiated() {
      return this.fetch_poid_status(
        'probability_instantiated',
        this.probability_poid
      )
    },
    probability_tracking_info() {
      return this.fetch_poid_tracking_info(
        'probability_tracking_info',
        this.probability_poid
      )
    },
    summary_probabilities_poid() {
      let pti = (this.tracking_info || {}).summary_proba_poid || -1
      return pti
    },
    summary_probabilities() {
      let spoid = this.summary_probabilities_poid
      let data = this.fetch_poid_data('summary_probabilities_poid', spoid)
      // console.log(JSON.stringify(data))
      let res = {
        observation_percentage: [],
        probability_threshold: [],
      }
      if (data) {
        let obs = (data || {}).observation_percentage
        let observation_percentages = []
        for (let key of extended_dict_keys(obs)) {
          let val = obs.getValue(key)
          let tmp = {
            obs_percentage: key,
            nb_samples: val[0],
            proba: val[1].toFixed(2),
          }
          observation_percentages.push(tmp)
        }
        let prob = (data || {}).proba_threshold
        let prob_threshold = []
        for (let key of extended_dict_keys(prob)) {
          let val = prob.getValue(key)
          let tmp = { proba_wanted: key, nb_samples: val }
          prob_threshold.push(tmp)
        }
        res = {
          observation_percentage: observation_percentages,
          probability_threshold: prob_threshold,
        }
      }
      return res
    },
    summary_probabilities_observation_percentage() {
      let res = (this.summary_probabilities || {}).observation_percentage
      return res
    },
    summary_probabilities_probability_threshold() {
      let res = (this.summary_probabilities || {}).probability_threshold
      return res
    },
    summary_feature_importance() {
      let fiti = ((this.tracking_info || {}).feature_importance || {}).final
      let res = [{ name: this.$t('waiting_computation'), value: '' }]
      if (fiti) {
        res = []
        let nb_best_features = Math.min(fiti.length, 5)
        let best_features = fiti.slice(0, nb_best_features)
        for (let info of best_features) {
          let infoo = { name: info[0], value: info[1].toFixed(2) }
          res.push(infoo)
        }
      }
      return res
    },
  },
}
</script>
